/**
 * The route tree, hand-authored (00-contracts §14.1, §14.2).
 *
 * Code-based rather than generated: `routeTree.gen.ts` is a merge-conflict magnet with several
 * agents in the same repository, and only a code-based route can carry `staticData` — which is
 * how the topbar knows a page's name without every page having to set it.
 *
 * Nesting: `root → public | authenticated → shell → settings`.
 * APPEND-ONLY for parallel agents: add routes at the marked insertion points, never reorder.
 */
import {
  createRootRouteWithContext,
  createRoute,
  lazyRouteComponent,
  notFound,
  redirect,
} from '@tanstack/react-router';

import {
  loadMe,
  requireAnonymous,
  requireConsent,
  requireAccountOpen,
  requireMembership,
  refuseGuest,
  requireCollectionPermission,
  requirePermission,
  requireSession,
} from '@/auth/guards';
import { validateAccessSearch, type AccessSearch } from '@/features/advance-access/access-search';
import { validateCalendarSearch, type CalendarSearch } from '@/features/calendar/search-params';
import { validateContactsSearch, type ContactsSearch } from '@/features/contacts/search-params';
import { validateInboxSearch, type InboxSearch } from '@/features/inbox/search-params';
import {
  validatePerformancesSearch,
  type PerformancesSearch,
} from '@/features/performances/search-params';
import {
  PERFORMANCE_TAB_IDS,
  type PerformanceTabId,
} from '@/features/performances/performance-tabs';
import { PEOPLE_TAB_IDS, type PeopleTabId } from '@/features/people/people-tabs';
import {
  EDITION_SETTINGS_TAB_IDS,
  type EditionSettingsTabId,
} from '@/features/festivals/edition-settings-tabs';
import { SUMMARY_TABS, type SummaryTab } from '@tvitvi/shared';
import { validateTasksSearch, type TasksSearch } from '@/features/tasks/search-params';
import {
  validateShowSearch,
  validateShowsSearch,
  type ShowSearch,
  type ShowsSearch,
} from '@/features/org-concerts/search-params';
/* Bookingsidens fane og artistregisterets filtre (QA-runde 12, #24d). */
import { validateBookingSearch } from '@/features/booking-register/search-params';
import {
  validateOfficialSearch,
  type OfficialSearch,
} from '@/features/control-room/official/official-search';
import {
  validateControlRoomVenuesSearch,
  type ControlRoomVenuesSearch,
} from '@/features/control-room/venues/venues-search';
/*
 * Ressurser validerer `?view=` mot sidens egen liste — se `RESOURCES_VIEWS`. Fra den lette fila,
 * ikke fra `ResourcesPage`: den importen dro hele siden inn i hovedbunten.
 */
import { RESOURCES_VIEWS, type ResourcesView } from '@/features/resources/resources-views';
import { safeRedirect } from '@/auth/redirect';
/* Spillesteder (agent B): fanen, visningen og filtrene i adressen, og salens fane og måned. */
import {
  validateVenueRoomSearch,
  validateVenuesSearch,
  type VenueRoomSearch,
  type VenuesSearch,
} from '@/features/venue-market/search-params';
import { validateEventsSearch, type EventsSearch } from '@/features/venue/events-search';
import { validateRoomsSearch, type RoomsSearch } from '@/features/venue/rooms-search';
import { validateEventSearch, type EventSearch } from '@/features/venue/event-tabs';
import {
  validateConcertPageSearch,
  type ConcertPageSearch,
} from '@/features/concerts/concert-tabs';
import { validateVenueCrewSearch, type VenueCrewSearch } from '@/features/venue-crew/search-params';
import {
  validateVenueRequestsSearch,
  type VenueRequestsSearch,
} from '@/features/date-requests/venue-requests-search';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
import { actorFromMe, allows } from '@/auth/actor';
import { organizationKindOf } from '@/auth/organization-kind';
import {
  settingsLandingPath,
  settingsSectionLandingPath,
} from '@/features/settings/settings-sections';
import {
  prefetchAgencyArtists,
  prefetchEditionCalendar,
  prefetchEditionPeople,
  prefetchEditionPerformances,
  prefetchEditionSummary,
  prefetchControlRoomOverview,
  prefetchShell,
  prefetchVenuePeople,
  prefetchVenueRooms,
} from './route-prefetch';

import { AuthenticatedLayout, ShellLayout } from './layouts';
import { RootLayout } from './root-layout';
import type { RouterContext } from './router-context';

/*
 * THERE IS NO LONGER A `comingSoon` HELPER HERE, and its absence is the milestone.
 *
 * It existed for "the one page behind every menu entry that is visible but not built yet", and
 * every menu entry now has a real page: Booking was the last, on 31 August. The helper went with
 * it rather than being left behind — a factory nothing calls is a factory somebody will call again
 * by mistake, and a menu entry pointing at an empty page is worse than no entry at all.
 *
 * `ComingSoonPage` itself still exists and is still mounted by `EditionComingSoonPages` for the
 * årgang's Economy shell, which is genuinely not built.
 */

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

// ---------------------------------------------------------------- public (anonymous only)

const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  beforeLoad: () => requireAnonymous(),
  component: lazyRouteComponent(() => import('@/features/auth/AuthLayout'), 'AuthLayout'),
});

const signInRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/sign-in',
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const raw = search['redirect'];
    if (typeof raw !== 'string' || raw.length === 0) return {};
    // Validated at the edge of the router, so no consumer downstream can be handed a bad target.
    return { redirect: safeRedirect(raw) };
  },
  component: lazyRouteComponent(() => import('@/features/auth/SignInPage'), 'SignInPage'),
});

/*
 * `/sign-up` STO HER TIL 20. SEPTEMBER (K-12).
 *
 * Atle: «"Har du ikke konto? Opprett konto" skal bort. Tvitvi er kun tilgjengelig via invitasjon,
 * og blir en betalt tjeneste.» Ruta lot hvem som helst lage en konto, og med e-postbekreftelsen av
 * (sak 132) var den kontoen ferdig bekreftet på en adresse man ikke eide.
 *
 * Kontoen lages nå på ett sted: `/bli-med/<token>`, som krever et gyldig invitasjonstoken.
 * `SignUpPage.tsx` er slettet med ruta — en side ingen rute peker på er kode ingen vedlikeholder.
 *
 * MEN RUTA VAR BARE DØRA. GoTrue-endepunktet står fortsatt åpent for et rått `signUp()`-kall, og
 * det er `app.before_user_created` som lukker det. Se sak 169.
 */

const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/forgot-password',
  /*
   * `redirect` BÆRES GJENNOM HELE TILBAKESTILLINGEN (QA14 runde 2, Q4).
   *
   * En som har konto fra før og ikke husker passordet, møtte «Logg inn og bli med» uten en vei
   * videre. Nå finnes lenken — og den må ta deg TILBAKE til invitasjonen etterpå, ikke til en tom
   * oversikt der du må finne e-posten på nytt. Samme form og samme `safeRedirect` som /sign-in.
   */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const raw = search['redirect'];
    if (typeof raw !== 'string' || raw.length === 0) return {};
    return { redirect: safeRedirect(raw) };
  },
  component: lazyRouteComponent(
    () => import('@/features/auth/ForgotPasswordPage'),
    'ForgotPasswordPage',
  ),
});

// ------------------------------------------- root level (the link already authenticated them)

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  /* Andre halvdel av Q4: målet kom med i lenken e-posten bar, og brukes når passordet er satt. */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const raw = search['redirect'];
    if (typeof raw !== 'string' || raw.length === 0) return {};
    return { redirect: safeRedirect(raw) };
  },
  component: lazyRouteComponent(
    () => import('@/features/auth/ResetPasswordPage'),
    'ResetPasswordPage',
  ),
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: lazyRouteComponent(
    () => import('@/features/auth/AuthCallbackPage'),
    'AuthCallbackPage',
  ),
});

/**
 * The design demo (Phase 1 step 0). DEV ONLY, and dev-only in the build sense, not just the
 * routing sense: `import.meta.env.DEV` is a compile-time constant, so in a production
 * build this whole expression collapses to `[]`, the dynamic import disappears with it, and
 * Rollup never emits the chunk. There is no nav entry for it either: it is a reviewing surface
 * for the component set, not a page in the product, and it renders outside the shell so it needs
 * neither a session nor a running API.
 */
const designRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/design',
        component: lazyRouteComponent(
          () => import('@/features/design/DesignSystemPage'),
          'DesignSystemPage',
        ),
      }),
    ]
  : [];

/**
 * Det offentlige påmeldingsskjemaet (PEOPLE.md, fane 2).
 *
 * At the ROOT rather than under `publicLayoutRoute`: that layout carries `requireAnonymous()`,
 * which sends a signed-in user away — and a production coordinator who is signed in must be able
 * to open their own form to check it. This page needs no session and refuses none.
 */
const publicSignupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/skjema/$token',
  component: lazyRouteComponent(
    () => import('@/features/people/PublicSignupPage'),
    'PublicSignupPage',
  ),
});

/**
 * Innløsningen av en byråinvitasjon (vedtak 93 og 99).
 *
 * At the ROOT, like `/skjema/$token` above it, and for the same reason: `publicLayoutRoute`
 * carries `requireAnonymous()` and would send a signed-in agent away — and a signed-in agent is
 * exactly who accepts one of these. The page needs no session and refuses none; the preview is
 * public and the accept is refused by the API without one.
 */
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitasjon/$token',
  component: lazyRouteComponent(
    () => import('@/features/organizations/InvitationRedemptionPage'),
    'InvitationRedemptionPage',
  ),
});

/**
 * LENKESIDEN (sak 232, T6): det en lenkeperson ser, uten innlogging. På roten av samme grunn som
 * `/invitasjon/$token` over: siden trenger ingen sesjon og nekter ingen — en innlogget arrangør
 * skal kunne åpne lenken hen nettopp laget for å se hva mottakeren ser.
 */
const shareLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/lenke/$token',
  component: lazyRouteComponent(() => import('@/features/share-link/LinkPage'), 'LinkPage'),
});

/**
 * DEN PERSONLIGE SIDEN (persontypene §3.1, 1090): den faste lenken i hver e-post. Samme side og samme
 * token som `/lenke`, som virker videre for lenkene som alt er sendt.
 */
const personalPageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/side/$token',
  component: lazyRouteComponent(() => import('@/features/share-link/LinkPage'), 'LinkPage'),
});

/**
 * SIGNERINGSSIDEN (sak 274, C2 punkt 1): en part uten konto signerer via engangslenke og kode. På
 * roten av samme grunn som lenkesiden: ingen sesjon trengs, og en innlogget skal også kunne åpne den.
 */
const contractSigningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signer/$token',
  component: lazyRouteComponent(
    () => import('@/features/contracts/PublicSigningPage'),
    'PublicSigningPage',
  ),
});

/**
 * Innløsingen av en BRUKERINVITASJON (Del 4.4) — ikke den samme som `/invitasjon/$token` over.
 *
 * Den er to ORGANISASJONER som kobles, og den som godtar er innlogget. Denne er et MENNESKE som
 * skal inn i et hus for første gang, og som kanskje ikke har en Tvitvi-konto i det hele tatt —
 * så innloggingen skjer på siden selv. Se komponenten for hvorfor den ikke kan skje på `/sign-in`.
 *
 * På roten, som de tre over, og av samme grunn: `publicLayoutRoute` bærer `requireAnonymous()`, og
 * en som alt er innlogget i et annet hus skal kunne løse inn en invitasjon til et nytt.
 */
const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bli-med/$token',
  component: lazyRouteComponent(
    () => import('@/features/organizations/JoinOrganizationPage'),
    'JoinOrganizationPage',
  ),
});

/**
 * ARTISTENS EGEN PLAN (vedtak 103.2, migrering 0167).
 *
 * PAA ROTEN, som de to over og av samme grunn: publicLayoutRoute baerer requireAnonymous() og
 * ville sendt en innlogget agent bort — og en agent som vil se hva artisten faktisk faar se er
 * innlogget. Siden trenger ingen sesjon og nekter ingen.
 */
const artistScheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/artistplan/$token',
  /* Sak 216.1: lenken er erstattet av innloggingen. Adressen lever, så en gammel lenke sier «logg inn». */
  component: lazyRouteComponent(
    () => import('@/features/agency/ArtistLinkRetiredPage'),
    'ArtistLinkRetiredPage',
  ),
});
const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/$slug',
  component: lazyRouteComponent(
    () => import('@/features/legal/LegalDocumentPage'),
    'LegalDocumentPage',
  ),
});
/* Én versjon, den `document_url` peker på (QA13 punkt 9). Samme side; versjonen står i adressen. */
const legalVersionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/$slug/$version',
  component: lazyRouteComponent(
    () => import('@/features/legal/LegalDocumentPage'),
    'LegalDocumentPage',
  ),
});

/**
 * GODKJENNINGSSIDEN FOR AI-TILKOBLINGER (B3/B5): AI-appen sender nettleseren hit fra
 * `/v1/oauth/authorize`. På roten, ikke under skallet: den har innloggingens ramme og trenger ingen
 * organisasjon — hvilke organisasjoner tilkoblingen får, er det brukeren velger her.
 *
 * Innlogging kreves for en forespørsel, og `requireSession` tar med hele adressen (med `request`)
 * til /sign-in og tilbake. En feil API-et sender hit (`?error=invalid_client`) finnes før noen
 * forespørsel, og vises uten innlogging.
 */
const connectConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connect/consent',
  validateSearch: (search: Record<string, unknown>): { request?: string; error?: string } => {
    const request = search['request'];
    const error = search['error'];
    return {
      ...(typeof request === 'string' && UUID.test(request) ? { request } : {}),
      ...(typeof error === 'string' && error.length > 0 && error.length <= 64 ? { error } : {}),
    };
  },
  beforeLoad: async ({ location, search }) => {
    if (search.error === undefined) await requireSession(location);
  },
  component: lazyRouteComponent(() => import('@/features/ai/ConsentPage'), 'ConsentPage'),
});

// ---------------------------------------------------------------- authenticated

const authenticatedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async ({ location, context }) => {
    await requireSession(location);
    // One /v1/me for the whole subtree, handed down through router context.
    const me = await loadMe(context.queryClient);
    /* 284.5 (1151): en konto som venter på slettingen, har bare siden med «Angre». */
    requireAccountOpen(me, location.pathname);
    return { me };
  },
  component: AuthenticatedLayout,
});

/* 284.5 (1151): «Kontoen din slettes» med «Angre». Lenken i e-posten går hit. */
const accountClosingRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/konto/angre',
  component: lazyRouteComponent(
    () => import('@/features/settings/AccountClosingPage'),
    'AccountClosingPage',
  ),
});

const consentRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/onboarding/consent',
  component: lazyRouteComponent(() => import('@/features/auth/ConsentGatePage'), 'ConsentGatePage'),
});

/**
 * Landingsplassen for en innlogget person UTEN et eneste medlemskap.
 *
 * Stien er den samme som før, men siden er en annen: «Opprett organisasjon» er fjernet (sak B3),
 * og det som står igjen forteller hva tilstanden er. Se `NoOrganizationPage`.
 */
const noOrganizationRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/onboarding/organization',
  beforeLoad: ({ context, location }) => {
    if (context.me) requireConsent(context.me, location.pathname);
  },
  component: lazyRouteComponent(
    () => import('@/features/organizations/NoOrganizationPage'),
    'NoOrganizationPage',
  ),
});

// ---------------------------------------------------------------- the signed-in shell

const shellLayoutRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  id: 'shell',
  beforeLoad: ({ context, location }) => {
    if (!context.me) return {};
    requireConsent(context.me, location.pathname);
    /*
     * STILL NO FESTIVAL PREFETCH, AND NOW FOR A DIFFERENT REASON. Until fase 2 there was no
     * endpoint to prefetch from (§16 C-24). There is one now — `GET /v1/festivals` — and the
     * shell deliberately does not await it: the sidebar's festival section owns that query and
     * draws its own skeleton, so awaiting it here would hold the WHOLE shell, every page in it,
     * behind a list that decorates the left edge. `CACHE.reference` means it is asked for once
     * per session anyway.
     */
    return { organizationId: requireMembership(context.me) };
  },
  /* Skallets egne spørringer starter mens sidekoden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchShell(context.queryClient, context.me);
  },
  component: ShellLayout,
});

const overviewRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/',
  staticData: { navId: 'overview' },
  component: lazyRouteComponent(() => import('@/features/overview/OverviewPage'), 'OverviewPage'),
});

/**
 * Threads, and the messages inside them. `thread:list` is the cell that opens the page; the API
 * narrows the result to threads the caller participates in, which is what keeps an external user
 * from seeing a thread they are not part of (the plan's acceptance line for this page).
 */
const inboxRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/inbox',
  staticData: { navId: 'inbox' },
  validateSearch: (search: Record<string, unknown>): InboxSearch => validateInboxSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'thread:list');
  },
  component: lazyRouteComponent(() => import('@/features/inbox/InboxPage'), 'InboxPage'),
});

/**
 * Every task assigned to me, across festivals. `task:list` opens it; the API narrows a `limited`
 * caller to their own tasks, which is the plan's acceptance line for this page.
 */
const tasksRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/tasks',
  staticData: { navId: 'tasks' },
  validateSearch: (search: Record<string, unknown>): TasksSearch => validateTasksSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'task:list');
  },
  component: lazyRouteComponent(() => import('@/features/tasks/TasksPage'), 'TasksPage'),
});

/**
 * Contacts (Phase 1, page 1).
 *
 * `beforeLoad` is the load-bearing line, not the component: hiding the menu entry is UX, and this
 * is the refusal a typed-in address meets (decision 0005, step 4). The API refuses again on every
 * request — this only saves the reader from walking into that wall.
 *
 * The search box and the tag filter live in `validateSearch`, so a narrowed list is a link and
 * survives a reload, and nothing downstream ever sees an unvalidated query.
 */
const contactsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/contacts',
  staticData: { navId: 'contacts' },
  validateSearch: (search: Record<string, unknown>): ContactsSearch =>
    validateContactsSearch(search),
  beforeLoad: ({ context, search }) => {
    /*
     * §5.4 (27.10): DET PERSONLIGE HUSET HAR INGEN KONTAKTER. Lista viste personen selv som «Kollega»
     * (hen er det eneste medlemmet). De personlige koblingene står på Profil (`MyConnectionsCard`).
     */
    if (context.me && organizationKindOf(context.me) === 'personal')
      throw redirect({ to: '/profile' });
    if (context.me) requirePermission(context.me, 'contact:list');
    /* A2 (22. oktober): medlemmene bor i Innstillinger › Medlemmer. Den gamle adressen sender dit. */
    if (search.tab === 'organization') {
      const { tab: _tab, ...rest } = search;
      /* Uten medlemslista (Begrenset) er Kontakter stedet, ikke en «Ingen tilgang» (klikkrunde 3). */
      const actor = context.me ? actorFromMe(context.me) : null;
      throw redirect(
        allows(actor, 'membership:list')
          ? { to: '/settings/members', search: rest }
          : { to: '/contacts', search: rest },
      );
    }
  },
  component: lazyRouteComponent(() => import('@/features/contacts/ContactsPage'), 'ContactsPage'),
});

/**
 * Booking, the fase 2 half (PAGES.md [F2/senere]).
 *
 * `performance:list` AND NO NEW CELL, which is the same call the guest list made: this page belongs
 * to one thing that already has cells. «Status per artist» IS `performance_status`, and putting an
 * act on the wishlist is the step before `wishlist` — the first move of booking them.
 *
 * The consequence worth naming: `performance:list` is `granted` for an external, so the register
 * refuses them. That is right for a page whose whole subject is who this organisation wants and
 * what it paid — «prisen én arrangør får deles aldri med andre arrangører», in the plan's words.
 */
const bookingRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/booking',
  staticData: { navId: 'booking' },
  /**
   * Fanen OG ARTISTREGISTERETS FILTRE ligger i URL-EN og ikke i `useState`.
   *
   * En fane i komponentens tilstand overlever ikke en omlasting og kan ikke lenkes til — man kan
   * ikke sende «se på programmeringa» til noen. Kontakter, Lineup og Oppsummering legger alle sin i
   * URL-en, og dette er den samme regelen. En ukjent verdi faller stille tilbake til «Artister»
   * framfor å bli en feil: et gammelt bokmerke er ikke en feilsituasjon.
   *
   * QA-runde 12, #24d la filtrene til, og reglene for hva som er en gyldig bookingadresse bor i
   * `features/booking-register/search-params.ts` framfor her: de er seks parametere med hver sin
   * form, og rutetreet skal ikke vite hvilke sjangre som finnes.
   */
  validateSearch: validateBookingSearch,
  beforeLoad: ({ context }) => {
    if (context.me) {
      refuseGuest(context.me);
      /* G2 (0923): «Se Booking». Modulen Booking spør can() selv (G1). */
      requirePermission(context.me, 'booking:list');
    }
  },
  component: lazyRouteComponent(
    () => import('@/features/booking-register/BookingRegisterPage'),
    'BookingRegisterPage',
  ),
});

/**
 * ARTISTER — byråets side av bordet (vedtak 93 og 98.1).
 *
 * Samme celle som Booking, av samme grunn: rosteren og henvendelsene ER byråets bookingarbeid. En
 * `agency:*`-familie ville vært brytere som uttrykker en regel `performance:list` allerede
 * bestemmer.
 *
 * Ruta følger modulen Byrå (0483), som menyen: uten modulen svarer den som en manglende rettighet.
 * Før modulene ble valgt fantes den i begge hustyper og bare menyen skjulte den.
 */
const agencyArtistsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/artists',
  staticData: { navId: 'artists' },
  /**
   * To faner, og de ligger i URL-EN av samme grunn som bookingsidens gjør: «se på forespørslene»
   * skal kunne sendes til noen. `roster` er standarden — Atle, 9. september: «Jeg ser heller ingen
   * oversikt over alle artistene mine. Det står kun henvendelsene mine der.»
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: 'roster' | 'enquiries'; enquiry?: string } => {
    const raw = search['tab'];
    /* `enquiry`: lenken fra innboksen, varselet og «kopier lenke» (QA runde 5). Siden finner artisten. */
    const enquiry = typeof search['enquiry'] === 'string' ? search['enquiry'] : undefined;
    const tab = raw === 'enquiries' ? ('enquiries' as const) : ('roster' as const);
    return enquiry === undefined ? { tab } : { tab, enquiry };
  },
  beforeLoad: ({ context }) => {
    if (context.me) {
      /* G2 (0923): «Se artistene». Uten modulen Byrå svarer can() nei (G1). */
      requirePermission(context.me, 'artist:list');
    }
  },
  /* Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchAgencyArtists(context.queryClient, context.me, location.search);
  },
  component: lazyRouteComponent(
    () => import('@/features/agency/AgencyArtistsPage'),
    'AgencyArtistsPage',
  ),
});

/**
 * The organisation's own shelf (Documents [F2]).
 *
 * `document:list` opens it — `yes/yes/yes/dept/grant`. A `limited` user therefore REACHES this page
 * and is narrowed inside it, seeing the folders with no department plus their own departments'.
 * That is the point of the page rather than an exception to it: the folder that holds the
 * evacuation plan is open, and the one that holds the contracts is not.
 *
 * The cell did not exist until Documents was built. Both these routes carried a note saying the
 * guard would move here the day it got a rule and a matrix row; this is that day.
 */
const documentsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/documents',
  staticData: { navId: 'documents' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'document:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/documents/DocumentsPage'),
    'DocumentsPage',
  ),
});

/**
 * One festival (fase 2, steg 2 — decision 0010: "Festival er et eget steg, og det kommer før
 * Editions").
 *
 * `festival:read` is the cell that opens it, and this guard is the refusal a typed-in address
 * meets — the sidebar hiding the link is UX, this is the door (decision 0005, step 4). The API
 * refuses again on every request; this only saves the reader from walking into that wall.
 *
 * The editions this festival holds are a later step. They land INSIDE this page, not under
 * Administrer, which is why there is no `/festivals/$festivalId/editions` route here to grow
 * into: an årgang is an edition of an event, and the event is the page.
 */
const festivalRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId',
  // `festivals` is a SECTION of the menu, not an entry, so navEntryById finds nothing for it
  // and the topbar used to fall through to Settings' name. The section's own name is the honest
  // breadcrumb; the festival's own name is the h1 on the page below it.
  /* «Festivaler › Leinesfjordfest › 2027» (QA13 28): siden melder festivalen og året selv. */
  staticData: { navId: 'festivals', titleKey: 'nav.section.festivalList' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'festival:read');
  },
  component: lazyRouteComponent(() => import('@/features/festivals/FestivalPage'), 'FestivalPage'),
});

/**
 * One årgang, under the festival it belongs to.
 *
 * NESTED UNDER THE FESTIVAL IN THE PATH, and that is the same call decision 0010 made for the
 * panel it is reached from: "Edition skal jo ligge i festivalen, ikke i administrasjon." An
 * årgang is an årgang OF an event, so its address says which event. The `festivalId` is not
 * decoration either — the year control lists the festival's other årganger, and it asks for them
 * with the id in the URL rather than waiting for the årgang itself to load.
 *
 * `edition:read` is the cell that opens it — `yes` for four roles, `grant` for external — and
 * this guard is the refusal a typed-in address meets (decision 0005, step 4). The API refuses
 * again on every request; this only saves the reader from walking into that wall.
 *
 * NOT A MENU ENTRY, so it names itself with `staticData.titleKey`. `navId: 'festivals'` keeps the
 * sidebar's festival section marked as where you are; `navEntryById` finds nothing for a SECTION,
 * which is exactly the fall-through that used to render "Innstillinger" here — see `Topbar`.
 */
const editionRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId',
  /* Samme oversikt som festivalsiden (QA13 28), og samme første ledd i brødsmula. */
  staticData: { navId: 'festivals', titleKey: 'nav.section.festivalList' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'edition:read');
  },
  component: lazyRouteComponent(() => import('@/features/festivals/EditionPage'), 'EditionPage'),
});

/**
 * The line-up of one årgang (fase 2, steg 4).
 *
 * UNDER THE ÅRGANG IN THE PATH, for the same reason the årgang is under the festival (decision
 * 0010): a performance is a performance OF a year OF an event, and its address says which. It is
 * declared here as a sibling of `editionRoute` rather than as a nested child, because that is how
 * every deeper route in this file is declared — `/tasks/$taskId` beside `/tasks`,
 * `/contacts/$contactId` beside `/contacts` — and because a child would inherit the årgang page's
 * component as a layout, which the line-up is not part of.
 *
 * `performance:list` is the cell that opens it — `yes` for four roles, `grant` for external — and
 * this guard is the refusal a typed-in address meets (decision 0005, step 4). NOT `edition:read`:
 * the two are separate cells and an organisation may edit either of them, so guarding on the
 * årgang's would be this page inventing a rule the matrix does not state. The page still reads the
 * årgang for its heading, and asks `edition:read` before it does.
 *
 * NOT A MENU ENTRY, so it names itself with `staticData.titleKey`; `navId: 'festivals'` keeps the
 * sidebar's festival section marked as where you are, exactly as the two routes above it do.
 *
 * The status chips, the stage and the order live in `validateSearch`, so a narrowed programme is a
 * link and survives a reload, and nothing downstream ever sees an unvalidated query.
 */
const editionPerformancesRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/performances',
  staticData: { navId: 'festivals', titleKey: 'performances.title' },
  validateSearch: (search: Record<string, unknown>): PerformancesSearch =>
    validatePerformancesSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'performance:list');
  },
  /* Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, params, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchEditionPerformances(context.queryClient, context.me, params, location.search);
  },
  component: lazyRouteComponent(
    () => import('@/features/performances/PerformancesPage'),
    'PerformancesPage',
  ),
});

/**
 * One booking, open — the page a line-up row leads to, and the frame its sections hang off.
 *
 * A SIBLING OF THE LINE-UP, NOT A CHILD OF IT, which is how every deeper route in this file is
 * declared — `/tasks/$taskId` beside `/tasks`, `/contacts/$contactId` beside `/contacts`. A child
 * would inherit the line-up's component as a layout, and a detail page is not drawn inside its
 * list.
 *
 * `performance:read` IS THE CELL THAT OPENS IT, not `performance:list`. Reading one booking and
 * listing a year's programme are separate cells and an organisation may edit either of them, so
 * guarding this on the list's would be this route inventing a rule the matrix does not state. It
 * is also the cell the guest list on this page consumes — `contracts/performance-guests.ts` is
 * explicit that the list is part of the booking and has no cells of its own.
 *
 * NO `validateSearch`. Nothing on this page keeps its state in the address bar: the guest
 * section's "ikke ankommet" chip is a mode somebody works in at a door for an evening, not a
 * narrowed view worth sending to a colleague, and the four sections still to come would all have
 * to share whatever shape were declared here.
 *
 * NOT A MENU ENTRY, so it names itself with `staticData.titleKey`; `navId: 'festivals'` keeps the
 * sidebar's festival section marked as where you are, exactly as the three routes above it do.
 */
const performanceDetailRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/performances/$performanceId',
  staticData: { navId: 'festivals', titleKey: 'performances.detail.title' },
  /**
   * WHICH TAB, IN THE URL (PERFORMANCE.md's eight faner).
   *
   * A reload lands where you were, and «se på reisa til denne acten» is a link somebody can send —
   * the same rule the task list's grouping follows. Validated at the edge like every other search
   * parameter here, so nothing downstream is handed a value the page does not have a tab for.
   *
   * It is deliberately NOT narrowed to the reader's permissions here: `beforeLoad` has no actor to
   * ask, and the page resolves an unreachable tab to the first one it may open. A route that
   * refused would turn a stale bookmark into an error page.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: PerformanceTabId; open?: string } & AccessSearch => {
    const raw = search['tab'];
    /* Den åpne oppgaven i Oppgaver-fanen — byråets egen, lest av festivalen (QA 23.09 punkt 2). */
    const open = search['open'];
    return {
      ...(typeof raw === 'string' && (PERFORMANCE_TAB_IDS as readonly string[]).includes(raw)
        ? { tab: raw as PerformanceTabId }
        : {}),
      ...(typeof open === 'string' && open !== '' ? { open } : {}),
      /* Tilgang-skuffen fra en lenke (ADVANCE-TILGANG.md §6, `?access=1&accessPerson=`). */
      ...validateAccessSearch(search),
    };
  },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'performance:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/performances/PerformanceDetailPage'),
    'PerformanceDetailPage',
  ),
});

/* ==============================================================================================
 * THE ÅRGANG'S OTHER FIVE SUBPAGES (NAVIGATION.md sone 2, PAGES.md "arrangementssider").
 *
 * NAVIGATION.md gives every arrangement the same submenu — Calendar, Tasks, Performances, People,
 * Documents, Settings — and the six are identical for a festival and for a venue until somebody
 * decides otherwise. Performances landed first and is declared above; these are the other five.
 *
 * ALL FIVE ARE SIBLINGS OF THE ÅRGANG, NOT CHILDREN OF IT, exactly as the line-up is and for the
 * reason written there: a child would inherit the årgang page's component as a layout, and none
 * of these is drawn inside it.
 *
 * TWO OF THEM ARE EXISTING PAGES, SCOPED. PAGES.md says so about Tasks in as many words — "samme
 * oppgavemodell som globalt, filtrert på arrangementet" — and the calendar's contract has offered
 * `editionId` since it was written. So `/…/tasks` and `/…/calendar` mount `TasksPage` and
 * `CalendarPage` through a thin component that reads the ids off the address, rather than forking
 * either of them.
 *
 * THE REST ARE REAL PAGES NOW, AND THE SISTE SKALLET ER BORTE (3. september).
 *
 * People, Documents, Settings og Economy var alle ærlige skall som tegnet `ComingSoonPage`. De tre
 * første forsvant med siden som erstattet dem. Økonomi ble en FANE på Oppsummering, og skallet ble
 * stående igjen — akkurat det filen selv advarte mot: «an export nothing routes to, a component
 * that still compiles, still has a test, and would quietly answer a route somebody re-pointed at
 * it by mistake». Kommentaren i skallet sa til og med at «Economy is the one that is genuinely not
 * built», om en side som var bygget.
 *
 * `EditionComingSoonPages` er slettet. Hvert menypunkt har nå en ekte side.
 *
 * NONE OF THEM IS A MENU ENTRY IN `navigation.ts`: the submenu is drawn from the organisation's
 * own arrangements, which are DATA. So each names itself with `staticData.titleKey`, the way the
 * four routes above do, and keeps `navId: 'festivals'` so the sidebar's arrangement section stays
 * marked as where you are.
 * ============================================================================================ */

/**
 * The årgang's calendar.
 *
 * `task:list` OPENS IT, AND IT IS THE SAME ARGUMENT `/calendar` MAKES. There is no `calendar:*`
 * cell because there is no calendar resource: what the grid draws is tasks counted per day, so
 * "may you see this page" is exactly the question the task list asks. The year narrows WHAT is
 * counted; it does not change WHO may count it, so this guard is the organisation calendar's,
 * unchanged. `edition:read` is deliberately NOT asked here — the page reads the årgang for its
 * heading and asks that cell itself before it does, and guarding the calendar on the årgang's
 * cell would be this route inventing a rule the matrix does not state.
 *
 * The month and the open day live in `validateSearch`, the same shape `/calendar` validates, so a
 * month inside a year is a link and survives a reload.
 */
const editionCalendarRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/calendar',
  staticData: { navId: 'festivals', titleKey: 'editionPages.calendar.title' },
  validateSearch: (search: Record<string, unknown>): CalendarSearch =>
    validateCalendarSearch(search),
  /* Kalenderens egen celle (rettelse B12) — se `event-nav.ts` for hvorfor lånet fra tasks var galt. */
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'event:list');
  },
  /* Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, params, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchEditionCalendar(context.queryClient, context.me, params, location.search);
  },
  component: lazyRouteComponent(
    () => import('@/features/calendar/EditionCalendarPage'),
    'EditionCalendarPage',
  ),
});

/**
 * The årgang's tasks.
 *
 * `task:list` opens it — the same cell `/tasks` guards on, because it is the same list asked one
 * question more. The API narrows a `limited` caller to their own rows and an `external` caller to
 * what they were granted through the very same `QueryScope`, whether or not a year is named, so
 * the year is a filter on top of that narrowing and never instead of it.
 *
 * The scope toggle, the grouping, the direction and the status chips live in `validateSearch`,
 * the same shape `/tasks` validates. The ÅRGANG IS NOT AMONG THEM: it is a path segment, which is
 * what makes it the one narrowing on that page no control can clear away.
 */
const editionTasksRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/tasks',
  staticData: { navId: 'festivals', titleKey: 'editionPages.tasks.title' },
  validateSearch: (search: Record<string, unknown>): TasksSearch => validateTasksSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'task:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/tasks/EditionTasksPage'),
    'EditionTasksPage',
  ),
});

/**
 * People — not built, and `contact:list` is the cell PAGES.md implies.
 *
 * "Alle mennesker i arrangementet, uansett kategori … Alle er referanser til Contacts, aldri nye
 * personer." The page is the contact register, narrowed to one year, so the question it will ask
 * is the register's own: `contact:list`, `yes/yes/yes/dept/no`. That is a real refusal rather
 * than a formality — an `external` user is turned away here exactly as they are at `/contacts`,
 * and a `limited` user will see their own departments' people rather than everybody.
 *
 * NOT `edition:read`: reading a year and reading the people in it are separate cells, and an
 * organisation may edit either of them. Guarding on the year's would be this route inventing a
 * rule the matrix does not state — the same call the line-up makes one screen over.
 */
const editionPeopleRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/people',
  staticData: { navId: 'festivals', titleKey: 'editionPages.people.title' },
  /**
   * HVILKEN FANE, I URL-EN (QA-punkt 9). Samme regel som programpostens åtte: en omlasting lander
   * der du var, og en kollega kan sende lenken. Validert i kanten, så siden aldri får en verdi den
   * ikke har en fane for — og en ukjent verdi faller stille tilbake framfor å bli en feilside.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tab?: PeopleTabId;
    view?: ResourcesView;
    at?: string;
    newPost?: true;
    person?: string;
  } => {
    const raw = search['tab'];
    const tab =
      typeof raw === 'string' && (PEOPLE_TAB_IDS as readonly string[]).includes(raw)
        ? (raw as PeopleTabId)
        : undefined;
    /* Ressurser-fanens visning (liste, uke, dag, kart) reiser i den samme adressen. */
    const view = search['view'];
    return {
      ...(tab === undefined ? {} : { tab }),
      ...(tab === 'resources' &&
      typeof view === 'string' &&
      (RESOURCES_VIEWS as readonly string[]).includes(view)
        ? { view: view as ResourcesView }
        : {}),
      /*
       * BROENE MELLOM FANENE (sak 210). `at` er en ressurs: på Stab og vakter er det vaktplanen for
       * den, på Ressurser er det panelet dens. `newPost` åpner «Ny post» med stedet utfylt — det
       * er «Skal noen jobbe her? Ja».
       */
      ...(typeof search['at'] === 'string' && UUID.test(search['at']) ? { at: search['at'] } : {}),
      ...(tab === 'staffing' && search['newPost'] === true ? { newPost: true as const } : {}),
      /* Personpanelet på Personer, åpnet fra en lenke (42: den ansvarlige på en ressurs). En kontakt. */
      ...(tab === 'participants' &&
      typeof search['person'] === 'string' &&
      UUID.test(search['person'])
        ? { person: search['person'] }
        : {}),
    };
  },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'contact:list');
  },
  /*
   * Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. Også det
   * `/…/resources` lander på: redirekten der kommer hit som en `enter` med `?tab=resources`.
   */
  loader: ({ context, params, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchEditionPeople(context.queryClient, context.me, params, location.search);
  },
  component: lazyRouteComponent(() => import('@/features/people/PeoplePage'), 'PeoplePage'),
});

/**
 * Ressurser (spesifikasjonen §7.1, trinn 1).
 *
 * `resource:list` OG IKKE `edition:read`: å lese et år og å lese tingene i det er to celler, og
 * en organisasjon kan flytte hver av dem. Å vokte på årgangens ville vært at denne ruta fant på en
 * regel matrisen ikke har — det samme valget lineup og Personer tar én skjerm unna.
 */
const editionResourcesRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/resources',
  staticData: { navId: 'festivals', titleKey: 'editionPages.resources.title' },
  /*
   * VISNINGEN STÅR I ADRESSEN (ux-koi1-2 funn 15).
   *
   * Rapporten: «`?view=week` ignoreres». Den sto i `useState`, så en omlasting landet på lista
   * igjen, og «se på uka» var ikke en lenke noen kunne sende. Det er den samme regelen hver annen
   * liste i produktet følger — og den er billig: fire lovlige verdier, validert i kanten.
   */
  validateSearch: (search: Record<string, unknown>): { view?: ResourcesView } => {
    const raw = search['view'];
    return typeof raw === 'string' && (RESOURCES_VIEWS as readonly string[]).includes(raw)
      ? { view: raw as ResourcesView }
      : {};
  },
  /*
   * ADRESSEN LEVER, SIDEN ER EN FANE (23. september). Ressurser flyttet inn i «Personer og
   * ressurser», rett etter vaktene. Bokmerker, lenker i tråder og hver spec som åpner
   * `/resources` lander på fanen, med visningen de ba om — samme grep som Kart fikk (B6).
   */
  beforeLoad: ({ context, params, search }) => {
    if (context.me) requirePermission(context.me, 'resource:list');
    const view = (search as { view?: unknown } | undefined)?.view;
    throw redirect({
      to: '/festivals/$festivalId/editions/$editionId/people',
      params: { festivalId: params.festivalId, editionId: params.editionId },
      search: {
        tab: 'resources',
        ...(typeof view === 'string' && (RESOURCES_VIEWS as readonly string[]).includes(view)
          ? { view: view as ResourcesView }
          : {}),
      },
      replace: true,
    });
  },
});

/**
 * KARTET — årgangens område (KOI Del 2).
 *
 * `edition:read` OG IKKE `resource:list`, og det er ikke det samme valget Ressurser tok én rute
 * over. Kartet er ÅRGANGENS: det tegner scener, ressurser, vaktposter og oppgaver på ett bilde, og
 * å vokte det på én av de fire ville stengt de tre andre for en rolle som skal se dem.
 *
 * Hva hvert LAG viser, avgjøres av RLS og ikke av denne porten: en `limited` bruker ser punktene på
 * sine egne oppgaver fordi `tasks` snevres for dem, og byrået ser scenen og bilen fordi migrasjon
 * 0222 deler nøyaktig de to. Ruta spør om lov til å åpne siden; radene svarer for seg selv.
 */
const editionMapRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/map',
  staticData: { navId: 'festivals', titleKey: 'map.title' },
  /*
   * `?open=` MÅ VALIDERES FOR Å KUNNE VIDERESENDES.
   *
   * En rute uten `validateSearch` får ikke ukjente parametre inn i `beforeLoad` — de står i
   * adressen og er borte for ruta. Uten denne linja ville redirekten under mistet nettopp det
   * den finnes for å ta med seg.
   */
  validateSearch: (search: Record<string, unknown>): { open?: string } =>
    typeof search['open'] === 'string' && search['open'] !== '' ? { open: search['open'] } : {},
  /*
   * ADRESSEN LEVER, SIDEN GJØR DET IKKE (rettelse B6).
   *
   * Atle, 17. september: kartet blir en fane under Oppsummering, og menypunktet fjernes. Men
   * adressen står i `map.spec.ts`, i `koi-shots.spec.ts`, i bokmerker og i hver lenke noen har
   * sendt i en tråd — så den beholdes og peker dit kartet nå er.
   *
   * `?open=` FØLGER MED, fordi det er hele grunnen ordren nevner den: en lenke til et punkt med
   * panelet åpent skal fortsatt åpne det panelet. `replace` gjør at «tilbake» går dit man kom
   * fra og ikke tilbake til en adresse som umiddelbart videresender.
   */
  beforeLoad: ({ context, params, search }) => {
    if (context.me) requireCollectionPermission(context.me, 'edition:read');
    const open = (search as { open?: unknown } | undefined)?.open;
    throw redirect({
      to: '/festivals/$festivalId/editions/$editionId/summary',
      params: { festivalId: params.festivalId, editionId: params.editionId },
      search: { tab: 'map', ...(typeof open === 'string' && open !== '' ? { open } : {}) },
      replace: true,
    });
  },
});

/**
 * One årgang's own folders (Documents (arrangement) [F2]).
 *
 * TWO CELLS, AND BOTH ARE ASKED. `edition:read` because these are the ÅRGANG'S documents and
 * somebody who may not read the year has no business in its folders; `document:list` because it is
 * the question the page itself answers.
 *
 * This route guarded on `edition:read` alone for two days under a note saying the guard would move
 * «the day `document:list` gets a rule and a matrix row». It has one now, so the note is discharged
 * rather than deleted: the narrower cell was added, and the door asks it.
 */
const editionDocumentsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/documents',
  staticData: { navId: 'festivals', titleKey: 'editionPages.documents.title' },
  beforeLoad: ({ context }) => {
    if (context.me) {
      requireCollectionPermission(context.me, 'edition:read');
      requirePermission(context.me, 'document:list');
    }
  },
  component: lazyRouteComponent(
    () => import('@/features/documents/DocumentsPage'),
    'EditionDocumentsPage',
  ),
});

/**
 * Oppsummering — årgangen på tvers av bookingene, med Økonomi som én av fire faner.
 *
 * ---------------------------------------------------------------------------------------------
 * DETTE VAR ØKONOMISIDEN FRAM TIL 3. SEPTEMBER, og navnebyttet er Atles: «Der økonomi er i dag
 * kaller vi siden heller oppsummering. Her har vi flere undersider som skal vise oppsummering av
 * all logistikk+++.» Stien byttet med navnet, fordi `/economy` som adressen til en side som mest
 * handler om reise, hotell og mat er en gjenganger av forvirring for alle som sender lenken videre.
 *
 * ---------------------------------------------------------------------------------------------
 * DØREN ER `performance:list`, IKKE `economy:read`, OG DET ER EN ENDRING SOM MÅ FORKLARES.
 *
 * Tre av de fire fanene leser reiser, senger og middager som ligger på bookingene — det er
 * programpostenes data, sett fra siden. `economy:read` er fortsatt cellen ØKONOMIFANEN spør om, og
 * fanen forsvinner for den som ikke har den (`TAB_PERMISSION` i `SummaryPage`); men å la hele
 * siden stå bak økonomicellen ville skjult logistikken bak en bryter satt for å skjule honorarer.
 *
 * Det som gjorde vedtak 0021 viktig gjelder fortsatt uendret: `economy:read` er JUSTERBAR, og å
 * skru den i Settings flytter fortsatt fanen. Standarden er fortsatt smal, fordi `PAGES.md` legger
 * hver kostnadslinje her og vedtak 0008 sender artisthonorarer hit MED VILJE, bort fra
 * kontaktregisteret nettopp fordi hver `member` leser det registeret i sin helhet.
 *
 * En ekstern kommer ikke inn noen av veiene: cellen deres på `performance:list` er `grant`, og et
 * grant uten en rad å peke på er et avslag (sak 79). API-et svarer det samme — se
 * `apps/api/test/app-boot.spec.ts`, hvor de tre rutene er skrevet ned med den begrunnelsen.
 *
 * ---------------------------------------------------------------------------------------------
 * HVILKEN FANE, I URL-EN, som på programpostens åtte faner: en omlasting lander der du var, og
 * «se på matlista for i år» er en lenke noen kan sende. Validert her i kanten, men IKKE snevret
 * inn til leserens rettigheter — `beforeLoad` har ingen aktør å spørre, og siden løser en fane den
 * ikke får åpne til den første den får. En rute som nektet ville gjort et gammelt bokmerke til en
 * feilside.
 */
const editionSummaryRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/summary',
  staticData: { navId: 'festivals', titleKey: 'summary.title' },
  /*
   * `open` KOM MED KARTET (rettelse B6): et klikk på et punkt åpner panelet oppå kartet, og
   * adressen er den man alt står på. Formen er en naken oppgave-id eller `resource:<id>` — se
   * `features/map/map-open-target.ts`, som er det ene stedet formen kjennes.
   */
  validateSearch: (search: Record<string, unknown>): { tab?: SummaryTab; open?: string } => {
    const raw = search['tab'];
    const open = search['open'];
    return {
      ...(typeof raw === 'string' && (SUMMARY_TABS as readonly string[]).includes(raw)
        ? { tab: raw as SummaryTab }
        : {}),
      ...(typeof open === 'string' && open !== '' ? { open } : {}),
    };
  },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'performance:list');
  },
  /* Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, params, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchEditionSummary(context.queryClient, context.me, params, location.search);
  },
  component: lazyRouteComponent(() => import('@/features/summary/SummaryPage'), 'SummaryPage'),
});

/**
 * The årgang's settings — not built, and `edition:update` is the cell PAGES.md names.
 *
 * "Kun for admin og superadmin." `edition:update` is `yes/yes/no/no/no`: superadmin and admin and
 * nobody else, which is that sentence expressed as a matrix row rather than as a role list in
 * code. It is also what the page will actually DO — every one of PAGES.md's items under this
 * heading is a change to how one year is configured — so the cell that opens it and the cell it
 * writes with are the same one, and the door cannot end up looser than the work behind it.
 *
 * This is the one guard in the six that refuses a `member`, and it is meant to: NAVIGATION.md
 * says an entry the reader may not have is ABSENT rather than greyed, so the submenu must hide
 * Settings from exactly these three roles and this route must refuse the address to exactly them.
 */
const editionSettingsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/festivals/$festivalId/editions/$editionId/settings',
  staticData: { navId: 'festivals', titleKey: 'editionPages.settings.title' },
  /*
   * HVILKEN FANE, I URL-EN — samme regel som Personer. Akkrediteringens «Endres i innstillingene»
   * lander på «Personfelt og roller» i stedet for «Generelt» (B.md, øktlogg).
   */
  validateSearch: (search: Record<string, unknown>): { tab?: EditionSettingsTabId } => {
    const raw = search['tab'];
    return typeof raw === 'string' && (EDITION_SETTINGS_TAB_IDS as readonly string[]).includes(raw)
      ? { tab: raw as EditionSettingsTabId }
      : {};
  },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'edition:update');
  },
  component: lazyRouteComponent(
    () => import('@/features/festivals/EditionSettingsPage'),
    'EditionSettingsPage',
  ),
});

/** One thread, open, with its messages. Linked from the list and from a notification. */
const threadDetailRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/inbox/$threadId',
  staticData: { navId: 'inbox' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'thread:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/inbox/ThreadDetailPage'),
    'ThreadDetailPage',
  ),
});

/**
 * EN DYPLENKE TIL ÉN OPPGAVE, OG DEN ÅPNER PANELET PÅ LISTA (avgjørelse 15).
 *
 * =================================================================================================
 * HER LÅ DET EN ANDRE DETALJVISNING TIL 17. SEPTEMBER, og den var to-versjoner-problemet i miniatyr.
 *
 * Den gamle sida hadde vedlegg, medansvarlige, «venter på» og kommentarer. `TaskPanel` hadde
 * delmål, etiketter, sted og aktivitet. Klikk på tittelen i lista tok deg til SIDA; kartet og
 * `?open=` tok deg til PANELET. Samme oppgave, to skjermer, ulike felt — og ingen av dem komplett.
 *
 * Avgjørelse 15 sier hva det skal være, og ruta består fordi hvert varsel, hver aktivitetslinje og
 * hver `#`-lenke peker hit. Den er bare ikke en side lenger.
 *
 * TILGANGEN SPØRRES FØRST. En videresending er ikke et svar på et nei: den som ikke har
 * `task:read` skal se avslaget, ikke en liste med et panel som feiler bak.
 */
const taskDetailRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/tasks/$taskId',
  staticData: { navId: 'tasks' },
  beforeLoad: ({ context, params }) => {
    if (context.me) requirePermission(context.me, 'task:read');
    throw redirect({ to: '/tasks', search: { open: params.taskId } });
  },
  component: () => null,
});

/*
 * SPILLESTEDET SETT FRA HUSET SELV (agent A, SPILLESTED-KONSERT). Egne adresser under `/venue`, og
 * ikke under festivalens årgang: saler og kvelder hører til spillestedet, ikke til ett år (lista
 * velger året selv). Oversikt, Personer, Ressurser og Innstillinger er festivalens egne sider.
 */
/*
 * QA15-S B3: SALER ER HUBEN. Alle saler eller én, med fanene Arrangementer · Forespørsler ·
 * Spesifikasjon (`?section=`). Fanenes egne filtre står ved siden av (`rooms-search.ts`).
 */
const venueRoomsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/rooms',
  staticData: { navId: 'venue', titleKey: 'venue.rooms.title' },
  validateSearch: (search: Record<string, unknown>): RoomsSearch => validateRoomsSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'venue:list', 'venue');
  },
  /* Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, params, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchVenueRooms(context.queryClient, context.me, params, location.search);
  },
  component: lazyRouteComponent(() => import('@/features/venue/RoomsPage'), 'RoomsPage'),
});

const venueRoomRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/rooms/$roomId',
  staticData: { navId: 'venue', titleKey: 'venue.rooms.title' },
  validateSearch: (search: Record<string, unknown>): RoomsSearch => validateRoomsSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'venue:read', 'venue');
  },
  /* Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, params, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchVenueRooms(context.queryClient, context.me, params, location.search);
  },
  component: lazyRouteComponent(() => import('@/features/venue/RoomsPage'), 'RoomsPage'),
});

/*
 * SAK 229: SPILLESTEDET ORGANISERES RUNDT ARRANGEMENTER, UTEN ÅRGANGER (V1).
 *
 * `/nights` og `/nights/$nightId` var adressene til 29. september. De står som videresendinger, så
 * bokmerker, varsler og lenker i tråder fortsatt lander — på arrangementet, med `replace` så
 * «tilbake» ikke går til en adresse som sender videre.
 */
const venueNightsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/nights',
  staticData: { navId: 'venue', titleKey: 'venue.nights.title' },
  beforeLoad: ({ context, params }) => {
    if (context.me) requirePermission(context.me, 'venue_night:list');
    throw redirect({
      to: '/venue/$festivalId/events',
      params: { festivalId: params.festivalId },
      replace: true,
    });
  },
  component: () => null,
});

const venueNightRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/nights/$nightId',
  staticData: { navId: 'venue', titleKey: 'venue.night.title' },
  beforeLoad: ({ context, params }) => {
    if (context.me) requirePermission(context.me, 'venue_night:read');
    throw redirect({
      to: '/venue/$festivalId/events/$nightId',
      params: { festivalId: params.festivalId, nightId: params.nightId },
      replace: true,
    });
  },
  component: () => null,
});

/**
 * Spillestedets Oversikt. `/festivals/$festivalId` sender et spillested hit, så brødsmula er
 * «Spillested» og ikke festivalseksjonens navn. `festival:read`, som festivalens forside.
 */
const venueHouseRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId',
  staticData: { navId: 'venue', titleKey: 'nav.section.venue' },
  beforeLoad: ({ context }) => {
    if (context.me) {
      /* QA6 K4, M6: en gjest har lesecellen, men ingenting her er delt — siden løy «alt har folk». */
      refuseGuest(context.me);
      requirePermission(context.me, 'festival:read');
    }
  },
  component: lazyRouteComponent(() => import('@/features/venue/VenueHousePage'), 'VenueHousePage'),
});

/**
 * «Arrangementer» var et eget menypunkt til QA15-S (B3). Lista bor nå under Saler, og adressen sender
 * videre med filtrene sine: en valgt sal blir salens egen side, resten følger med i søket.
 */
const venueEventsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/events',
  staticData: { navId: 'venue', titleKey: 'venue.nights.title' },
  validateSearch: (search: Record<string, unknown>): EventsSearch => validateEventsSearch(search),
  beforeLoad: ({ context, params, search }) => {
    if (context.me) requirePermission(context.me, 'venue_night:list');
    const { roomId, ...rest } = search;
    if (roomId !== undefined) {
      throw redirect({
        to: '/venue/$festivalId/rooms/$roomId',
        params: { festivalId: params.festivalId, roomId },
        search: { ...rest, section: 'events' },
        replace: true,
      });
    }
    throw redirect({
      to: '/venue/$festivalId/rooms',
      params: { festivalId: params.festivalId },
      search: { ...rest, section: 'events' },
      replace: true,
    });
  },
  component: () => null,
});

/** Arrangementet, med fanen i `?tab=`. Param-navnet er fortsatt `nightId` (koden heter `venue_night`). */
const venueEventRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/events/$nightId',
  staticData: { navId: 'venue', titleKey: 'venue.night.title' },
  validateSearch: (search: Record<string, unknown>): EventSearch => validateEventSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'venue_night:read');
  },
  component: lazyRouteComponent(() => import('@/features/venue/EventPage'), 'EventPage'),
});

/** Spillestedets Innstillinger — spillestedet selv, ikke et år. `festival:update`. */
const venueSettingsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/settings',
  staticData: { navId: 'venue', titleKey: 'venue.settings.title' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'festival:update');
  },
  component: lazyRouteComponent(
    () => import('@/features/venue/VenueSettingsPage'),
    'VenueSettingsPage',
  ),
});

/**
 * Spillestedets Personer: husets mannskap og vaktplanen på tvers av arrangementene (V3), uten
 * årgang. `crew_member:list` åpner den — `own` for `limited`, som mannskapet ellers.
 */
const venuePeopleRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/people',
  staticData: { navId: 'venue', titleKey: 'nav.venue.people' },
  validateSearch: (search: Record<string, unknown>): VenueCrewSearch =>
    validateVenueCrewSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) {
      /* QA6 K4, M6: husets mannskap deles ikke med en gjest. */
      refuseGuest(context.me);
      requirePermission(context.me, 'crew_member:list');
    }
  },
  /* Sidens spørringer starter mens koden lastes — ikke awaited. Se `route-prefetch.ts`. */
  loader: ({ context, params, location, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchVenuePeople(context.queryClient, context.me, params, location.search);
  },
  component: lazyRouteComponent(
    () => import('@/features/venue-crew/VenueCrewPage'),
    'VenueCrewPage',
  ),
});

/** Spillestedets Ressurser: husets register uten årgang (V2). `resource:list`. */
const venueResourcesRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/resources',
  staticData: { navId: 'venue', titleKey: 'nav.venue.resources' },
  beforeLoad: ({ context }) => {
    if (context.me) {
      /* QA6 K4, M6: husets ressursregister deles ikke med en gjest. */
      refuseGuest(context.me);
      requirePermission(context.me, 'resource:list');
    }
  },
  component: lazyRouteComponent(
    () => import('@/features/venue/VenueResourcesPage'),
    'VenueResourcesPage',
  ),
});

/*
 * Husets Datoforespørsler (agent C, skjerm 4). Fra QA15-S (B3) en fane under Saler; adressen sender
 * videre med `?tab=` og `?enquiry=`, så varsler og lenker i tråder fortsatt åpner forespørselen.
 */
const venueRequestsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venue/$festivalId/requests',
  staticData: { navId: 'venue', titleKey: 'dateRequests.venue.title' },
  validateSearch: (search: Record<string, unknown>): VenueRequestsSearch =>
    validateVenueRequestsSearch(search),
  beforeLoad: ({ context, params, search }) => {
    if (context.me) requirePermission(context.me, 'venue_enquiry:list');
    throw redirect({
      to: '/venue/$festivalId/rooms',
      params: { festivalId: params.festivalId },
      search: { ...search, section: 'requests' },
      replace: true,
    });
  },
  component: () => null,
});

/**
 * «Utseende» (Glass 2, PROMPT §5). Personens eget, som profilen: samme forelder, ingen
 * `beforeLoad` — innlogget er hele sjekken, og valgene bor i nettleseren, ikke i huset.
 * Et søsken av `/profile` og ikke et barn: profilen er en side, ikke et oppsett med `<Outlet>`.
 */
const appearanceRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/profile/appearance',
  staticData: { titleKey: 'appearance.title' },
  component: lazyRouteComponent(
    () => import('@/features/appearance/AppearancePage'),
    'AppearancePage',
  ),
});

/**
 * «Varsler» (F5/F51, runde 4. oktober): varslingsvalgene. Personens eget, som utseendet: samme
 * forelder, ingen `beforeLoad` — innlogget er hele sjekken, og valgene gjelder i alle husene.
 */
const notificationPreferencesRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/profile/notifications',
  staticData: { titleKey: 'notificationPreferences.title' },
  component: lazyRouteComponent(
    () => import('@/features/notifications/NotificationPreferencesPage'),
    'NotificationPreferencesPage',
  ),
});

/**
 * CONTROL ROOM (sak 238; het gründerportalen til 2. oktober): plattformadminens flate på tvers av
 * kundene, bare for en plattformadmin (`me.isPlatformAdmin`). Alle andre får «siden finnes ikke»,
 * som API-et svarer 404 — den skal ikke engang kunne oppdages. Uten `me` er svaret det samme: ingen
 * dør på antakelse. Døra er layoutens `beforeLoad`, som hver side under arver.
 *
 * Sidene bor i `features/control-room/<område>/`, med de filene og eksportnavnene
 * `docs/qa/control-room/BRIEF.md` avtaler — flere agenter bygger dem.
 */
const controlRoomLayoutRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  id: 'control-room',
  staticData: { titleKey: 'controlRoom.title' },
  beforeLoad: ({ context }) => {
    if (context.me?.isPlatformAdmin !== true) throw notFound();
  },
  component: lazyRouteComponent(
    () => import('@/features/control-room/ControlRoomLayout'),
    'ControlRoomLayout',
  ),
});

const controlRoomOverviewRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room',
  staticData: { titleKey: 'controlRoom.title' },
  /* Layoutens kode og sidens kode lastes etter hverandre; spørringene venter ikke på noen av dem. */
  loader: ({ context, cause }) => {
    if (cause !== 'enter' || !context.me) return;
    prefetchControlRoomOverview(context.queryClient, context.me);
  },
  component: lazyRouteComponent(
    () => import('@/features/control-room/overview/OverviewPage'),
    'OverviewPage',
  ),
});

const controlRoomOrganizationsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/organisasjoner',
  staticData: { titleKey: 'controlRoom.nav.organizations' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/organizations/OrganizationsPage'),
    'OrganizationsPage',
  ),
});

const controlRoomOrganizationRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/organisasjoner/$organizationId',
  staticData: { titleKey: 'controlRoom.nav.organizations' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/organizations/OrganizationPage'),
    'OrganizationPage',
  ),
});

const controlRoomAdminsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/plattformadminer',
  staticData: { titleKey: 'controlRoom.nav.admins' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/admins/PlatformAdminsPage'),
    'PlatformAdminsPage',
  ),
});

/*
 * Artistskanning (QA13 #3): den offisielle artistlista og byråskanningen på én side, med fanene
 * Artister, Byråer, Må sjekkes og Kjøringer. Fanen, søket og artisten som er åpen står i adressen.
 */
const controlRoomArtistScanRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/artistskanning',
  staticData: { titleKey: 'controlRoom.nav.artistScan' },
  validateSearch: (search: Record<string, unknown>): OfficialSearch =>
    validateOfficialSearch(search),
  component: lazyRouteComponent(
    () => import('@/features/control-room/official/OfficialListPage'),
    'OfficialListPage',
  ),
});

/* De gamle adressene sender videre: Offisiell liste med fanen og søket, Byråskanning til Kjøringer. */
const legacyOfficialRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/offisiell-liste',
  validateSearch: (search: Record<string, unknown>): OfficialSearch =>
    validateOfficialSearch(search),
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/control-room/artistskanning', search, replace: true });
  },
  component: () => null,
});

const legacyScanRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/byraskanning',
  beforeLoad: () => {
    throw redirect({ to: '/control-room/artistskanning', search: { tab: 'runs' }, replace: true });
  },
  component: () => null,
});

const controlRoomOperationsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/drift',
  staticData: { titleKey: 'controlRoom.nav.operations' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/operations/OperationsPage'),
    'OperationsPage',
  ),
});

const controlRoomPermissionsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/rettighetsvalg',
  staticData: { titleKey: 'controlRoom.nav.permissions' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/permissions/PermissionChoicesPage'),
    'PermissionChoicesPage',
  ),
});

/* Tvitvis standarder (G4, 0942): det nye festivaler og organisasjoner får. */
const controlRoomDefaultsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/standarder',
  staticData: { titleKey: 'controlRoom.nav.defaults' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/defaults/PlatformDefaultsPage'),
    'PlatformDefaultsPage',
  ),
});

/* Atle 02.11 (1329): Control Room › Vilkår — de fire dokumentene og revisjonene deres. */
const controlRoomLegalRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/vilkar',
  staticData: { titleKey: 'controlRoom.nav.legal' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/legal/PlatformLegalPage'),
    'PlatformLegalPage',
  ),
});

/* QA15-S E (B10): Control Room › Spillesteder — registeret over norske spillesteder, som artistregisteret. */
const controlRoomVenuesRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/spillesteder',
  staticData: { titleKey: 'controlRoom.nav.venues' },
  validateSearch: (search: Record<string, unknown>): ControlRoomVenuesSearch =>
    validateControlRoomVenuesSearch(search),
  component: lazyRouteComponent(
    () => import('@/features/control-room/venues/VenueDirectoryPage'),
    'VenueDirectoryPage',
  ),
});

/* Sak 273.1 a: Control Room › Soundcharts. */
const controlRoomSoundchartsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/soundcharts',
  staticData: { titleKey: 'controlRoom.nav.soundcharts' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/soundcharts/SoundchartsPage'),
    'SoundchartsPage',
  ),
});

/* Atle 03.11: Control Room › Leverandører — kvoter, forbruk og fakturalenker. */
const controlRoomVendorsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/leverandorer',
  staticData: { titleKey: 'controlRoom.nav.vendors' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/vendors/VendorsPage'),
    'VendorsPage',
  ),
});

const controlRoomLogRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/control-room/logg',
  staticData: { titleKey: 'controlRoom.nav.log' },
  component: lazyRouteComponent(
    () => import('@/features/control-room/log/ActivityLogPage'),
    'ActivityLogPage',
  ),
});

/* De gamle stiene fra gründerportalen omdirigeres — bak den samme døra. */
const legacyFounderRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/grunder',
  beforeLoad: () => {
    throw redirect({ to: '/control-room' });
  },
  component: () => null,
});

const legacyFounderPermissionsRoute = createRoute({
  getParentRoute: () => controlRoomLayoutRoute,
  path: '/grunder/rettigheter',
  beforeLoad: () => {
    throw redirect({ to: '/control-room/rettighetsvalg' });
  },
  component: () => null,
});

// --- Phase 1 insertion point: shell routes ---

/**
 * «Koble til AI» (B5): nås fra ikonet i toppen, ikke fra menyen — menyrekkefølgen er Atles. Ingen
 * `beforeLoad`: tilkoblingene er personens egne, og bryterne leses av alle medlemmer.
 */
const connectAiRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/ai',
  staticData: { titleKey: 'ai.page.title' },
  component: lazyRouteComponent(() => import('@/features/ai/ConnectAiPage'), 'ConnectAiPage'),
});

/**
 * The totalkalender (fase 2, steg 3) — the organisation's calendar, one month at a time.
 *
 * `task:list` opens it, and that is not a stand-in for a missing `calendar:*` cell: there is no
 * calendar resource, only the organisation's tasks counted per day, so this is literally the
 * same question `/tasks` asks. The API narrows a `limited` caller's COUNTS through the same
 * `QueryScope` it narrows their list with — the contract is explicit that a count computed
 * without that narrowing is itself the leak.
 *
 * IT IS A MENU ENTRY, so it takes `navId` and the topbar reads the name from there — no
 * `titleKey`, which is for the routes that are deliberately not in the menu.
 *
 * The month and the open day live in `validateSearch`, so a month is a link and survives a
 * reload, and nothing downstream ever sees an unvalidated query.
 */
const calendarRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/calendar',
  staticData: { navId: 'calendar' },
  validateSearch: (search: Record<string, unknown>): CalendarSearch =>
    validateCalendarSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'event:list');
  },
  component: lazyRouteComponent(() => import('@/features/calendar/CalendarPage'), 'CalendarPage'),
});

/**
 * Konsertene personen står på, på tvers av band og byråer (sak 216). Ingen celle: svaret handler om
 * mennesket, og porten er `app.my_concerts()`, som bare svarer for personen selv.
 */
const concertsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/concerts',
  staticData: { navId: 'concerts' },
  component: lazyRouteComponent(() => import('@/features/concerts/ConcertsPage'), 'ConcertsPage'),
});

const concertDetailRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/concerts/$kind/$id',
  staticData: { navId: 'concerts' },
  /* Fanen og advance-underfanen i adressen (C, sak 229.8–231). */
  validateSearch: (search: Record<string, unknown>): ConcertPageSearch =>
    validateConcertPageSearch(search),
  component: lazyRouteComponent(() => import('@/features/concerts/ConcertPage'), 'ConcertPage'),
});

/**
 * DELTE ARTISTER (A9, 268.2 a): artistene som er delt med personen, på tvers av organisasjonene, inne i
 * skallet. Ingen celle: porten er `app.my_shared_artists()` og `app.shared_artist_view`, som bare
 * svarer mottakeren. `/delt/$grantId` står som før for de små delingene.
 */
const sharedArtistsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/shared-artists',
  staticData: { navId: 'sharedArtists' },
  component: lazyRouteComponent(
    () => import('@/features/shared-artists/SharedArtistsPage'),
    'SharedArtistsPage',
  ),
});

const sharedArtistRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/shared-artists/$grantId',
  staticData: { navId: 'sharedArtists' },
  /* Fanen, som på byråets artistside (A9). Siden faller tilbake til første fane omfanget gir. */
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search['tab'] === 'string' ? { tab: search['tab'] } : {},
  component: lazyRouteComponent(
    () => import('@/features/shared-artists/SharedArtistPage'),
    'SharedArtistPage',
  ),
});

/*
 * DELTE PRODUKSJONER (§5.1): det som er delt med meg fra organisasjoner jeg ikke er medlem av, én rad
 * per festival eller arrangør. Menneskets, ikke husets — porten er `app.my_shared_productions()`.
 */
const sharedProductionsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/shared-productions',
  staticData: { navId: 'sharedProductions' },
  component: lazyRouteComponent(
    () => import('@/features/shared-productions/SharedProductionsPage'),
    'SharedProductionsPage',
  ),
});

/** The page every later module links a person from. `contact:read` is its own matrix cell. */
const contactDetailRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/contacts/$contactId',
  staticData: { navId: 'contacts' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'contact:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/contacts/ContactDetailPage'),
    'ContactDetailPage',
  ),
});

/**
 * One firm (vedtak 100.3). Three segments, so it can never be mistaken for `/contacts/$contactId`
 * — a static child always wins over a parameter in TanStack's matcher, and this way the reader can
 * see which of the two a URL is without knowing any ids.
 *
 * `contact:read`, the same cell the register uses, because a firm is that register seen from the
 * side. The controller carries the whole argument.
 */
const companyDetailRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/contacts/companies/$companyId',
  staticData: { navId: 'contacts' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'contact:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/contacts/CompanyDetailPage'),
    'CompanyDetailPage',
  ),
});

/**
 * Én artist hos byrået — bandet, riderne og promoen (vedtak 96.1).
 *
 * `performance:list` åpner den, som resten av bookingregisteret: stamdataene er FELTER PÅ
 * ARTISTEN sett fra siden, ikke en egen makt. Skrivingene narrows inne i siden.
 */
const artistRosterRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/artists/$artistId',
  staticData: { navId: 'artists' },
  /**
   * Seks faner, og de ligger i URL-en: «se på kalenderen hennes» skal kunne sendes til noen.
   * En ukjent verdi faller stille tilbake til oversikten — et gammelt bokmerke er ikke en feil.
   *
   * `agreement` kom med ARTIST-AVTALE-ORDREN Del 1. Ruta slipper den gjennom; SIDEN avgjør om den
   * finnes, fordi `artist_agreement:read` er en cellesjekk og ikke noe adressen kan svare på.
   *
   * `date` peker kalenderen på en bestemt dag — kalenderstripa på Oversikt lenker med den (Del 2).
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tab?:
      'overview' | 'calendar' | 'bookings' | 'people' | 'documents' | 'marketplace' | 'agreement';
    date?: string;
    enquiry?: string;
  } => {
    const raw = search['tab'];
    const day = search['date'];
    /* Forespørselen som skal åpnes i dialogen — satt av `/artists?enquiry=` (QA runde 5). */
    const enquiry = typeof search['enquiry'] === 'string' ? search['enquiry'] : undefined;
    const date = typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : undefined;
    /* Persontypene §2.6: Stamdata ble Personer og Dokumenter. Et gammelt bokmerke på Stamdata gir Personer. */
    const tab =
      raw === 'roster'
        ? ('people' as const)
        : raw === 'calendar' ||
            raw === 'bookings' ||
            raw === 'people' ||
            raw === 'documents' ||
            raw === 'marketplace' ||
            raw === 'agreement'
          ? raw
          : ('overview' as const);
    return {
      tab,
      ...(date === undefined ? {} : { date }),
      ...(enquiry === undefined ? {} : { enquiry }),
    };
  },
  beforeLoad: ({ context }) => {
    /* Artisten i stallen (G2, 0923): «Se artistene», som lista. */
    if (context.me) requirePermission(context.me, 'artist:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/agency/ArtistRosterPage'),
    'ArtistRosterPage',
  ),
});

/**
 * DEN DELTE ADVANCEN (0133, ARTISTS.md §4.1 og §4.2).
 *
 * ================================================================================================
 * ADRESSEN GÅR GJENNOM ARTISTEN, slik Atle tegnet den 11. september:
 *
 *     Standing Ovation › Artister › Bendik › Advance
 *
 * En advance er noe som HENDER med en artist byrået representerer, ikke et selvstendig objekt i
 * menyen. Første forsøk la den på roten (`/advances/$advanceId`), og da hadde den ingen vei
 * tilbake til noe byrået kjenner.
 *
 * PROGRAMPOSTENS ID I ADRESSEN og ikke advancens: siden bak er festivalens egen
 * `PerformanceDetailPage`, og den slår opp på programposten. Advancen er raden som gir tilgangen,
 * ikke tingen man ser på.
 */
const agencyAdvanceRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  /*
   * ADVANCENS ID, IKKE PROGRAMPOSTENS (QA K2).
   *
   * Byrået kan opprette advancen før festivalen har programmert artisten (§4.1), og da finnes det
   * ingen programpost å legge i adressen. Ruta bar den likevel, så lenken byrået fikk etter
   * «Opprett advancen» pekte på en id som ikke var en programpost — og siden svarte «Programposten
   * finnes ikke».
   */
  path: '/artists/$artistId/advance/$advanceId',
  staticData: { navId: 'artists' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'performance:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/agency/AgencyAdvancePage'),
    'AgencyAdvancePage',
  ),
});

/**
 * YOUR ACCOUNT, AND IT IS NOT UNDER `/settings` ANY MORE.
 *
 * Two different things had been sharing one menu: your own name and language, and rules that
 * govern everybody in the organisation. Atle's call on 28 August separates them by where you
 * reach them from — the account lives behind your own avatar in the header, the organisation
 * lives in the folder structure on the left — so they no longer share a URL prefix either.
 *
 * Its own route rather than a child of `settingsLayoutRoute`, because it must NOT get the
 * organisation sub-menu: that menu is the thing being moved away from here.
 */
const profileRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/profile',
  // Not in the menu — it lives behind the avatar — so it names itself. See Topbar.
  staticData: { titleKey: 'nav.profile' },
  component: lazyRouteComponent(
    () => import('@/features/settings/ProfileSettingsPage'),
    'ProfileSettingsPage',
  ),
});

/**
 * «Mine vakter» (201.14) — where a notification about a shift lands. Since QA13 103 it is in the
 * personal group of the menu (`navId: 'myShifts'`), so it asks the menu's question too: `shift:list`,
 * the cell `/shifts/mine` is guarded by (decision 0005). Before, an external typing the address met
 * an error page (QA runde 5, M6).
 */
const myShiftsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/my-shifts',
  staticData: { navId: 'myShifts', titleKey: 'myShifts.title' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'shift:list');
  },
  component: lazyRouteComponent(() => import('@/features/people/MyShiftsPage'), 'MyShiftsPage'),
});

/**
 * «Mine eksporter» (docs/EKSPORT.md §8). Bak avataren som «Mine vakter»: det gjelder leseren, og det
 * er her varselet om en ferdig eksport lander. Ingen `beforeLoad`: lista er leserens egne rader (RLS).
 */
const exportsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/exports',
  staticData: { titleKey: 'export.ui.page.title' },
  component: lazyRouteComponent(() => import('@/features/export/ExportsPage'), 'ExportsPage'),
});

/** Organisation settings. Reached from the sidebar; the account is no longer one of its entries. */
const settingsLayoutRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  id: 'settings',
  // Named here rather than by a fallback in Topbar. It was THE page without a menu entry once,
  // which is how that fallback came to stand in for every later one.
  staticData: { titleKey: 'settings.title' },
  /* Husets innstillinger — budvilkår, satser, avdelinger — er ikke en ekstern sin (QA runde 5, H2). */
  beforeLoad: ({ context }) => {
    if (context.me) refuseGuest(context.me);
  },
  component: lazyRouteComponent(
    () => import('@/features/settings/SettingsLayout'),
    'SettingsLayout',
  ),
});

/**
 * `/settings` itself has no page — it forwards to the first page in the first folder this reader
 * can actually open.
 *
 * ASKED RATHER THAN HARDCODED, since 1 September. It was the literal `/settings/departments`,
 * which is right only for as long as `department:list` stays `yes` for everybody — and the matrix
 * is data an owner may edit. `settingsLandingPath` walks the same list the menu draws, so the
 * landing spot and the first visible entry are the same fact rather than two that can disagree.
 * Departments is still the answer for every role today; nothing moves until a cell does.
 */
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings',
  beforeLoad: ({ context }) => {
    /* Samme sperre som layouten; her også, så indeksen ikke videresender en ekstern et sted. */
    if (context.me) refuseGuest(context.me);
    throw redirect({
      to: settingsLandingPath(
        context.me ? actorFromMe(context.me) : null,
        organizationKindOf(context.me ?? null),
      ),
    });
  },
  component: () => null,
});

/**
 * THE FOLDERS THEMSELVES, and they exist so a section is something you can send to somebody.
 *
 * Atle, 1 September: «Del opp i mapper, det kan ikke være 1 lang scroll med innstillinger.» A
 * folder with no address is a heading; with one, «se på tilgangene» is a link. Each forwards to
 * its own first open page — so the address survives a reader who lacks the page it usually lands
 * on, and a folder that is empty for them lands them where the menu would have.
 *
 * No guard of their own: they resolve to a page, and that page's route asks its own question.
 */
const settingsOrganizationRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/organization',
  beforeLoad: ({ context }) => {
    const actor = context.me ? actorFromMe(context.me) : null;
    throw redirect({ to: settingsSectionLandingPath(actor, 'organization') });
  },
  component: () => null,
});

const settingsAccessRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/access',
  beforeLoad: ({ context }) => {
    const actor = context.me ? actorFromMe(context.me) : null;
    throw redirect({ to: settingsSectionLandingPath(actor, 'access') });
  },
  component: () => null,
});

const settingsDataRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/data',
  beforeLoad: ({ context }) => {
    const actor = context.me ? actorFromMe(context.me) : null;
    throw redirect({ to: settingsSectionLandingPath(actor, 'data') });
  },
  component: () => null,
});

/**
 * The adjustable-permission switch panel (fase 2, Settings).
 *
 * `role_permission:list` is `yes` for superadmin and `no` for all four other roles, so this
 * guard is one of the few in the app that refuses somebody who can reach every other page. The
 * settings menu hides the entry too (decision 0005), and the API refuses again on every request —
 * this only saves the reader from walking into that wall.
 */
const permissionsSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/permissions',
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'role_permission:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/settings/PermissionsSettings'),
    'PermissionsSettings',
  ),
});

/**
 * Innstillinger › Delinger (F9, runde 4. oktober): hvem utenfor organisasjonen som har tilgang til
 * hva. `share:list` åpner den — standard superadmin — og API-et spør den samme cella igjen.
 */
const sharesSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/shares',
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'share:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/sharing/SharesOverviewPage'),
    'SharesOverviewPage',
  ),
});

/**
 * Innstillinger › AI (B4): bryterne «AI-tilgang» og «Skrivetilgang for AI», og organisasjonens
 * AI-tilkoblinger. Bare superadmin — den låste cella `membership:manage_roles`, den samme API-et
 * spør (B3 valg 3).
 */
const aiSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/ai',
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'membership:manage_roles');
  },
  component: lazyRouteComponent(() => import('@/features/ai/AiSettingsPage'), 'AiSettingsPage'),
});

/**
 * Innstillinger › Organisasjon (QA12 #9). `organization:read` åpner den — ja for alle fem rollene
 * — og `organization:update` avgjør INNE i siden om feltene er skrivbare. Å gate ruta på skrivecella
 * ville skjult organisasjonsnummeret for staben som skal føre det på en faktura.
 */
const organizationDetailsSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/organization-details',
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'organization:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/settings/OrganizationSettings'),
    'OrganizationSettings',
  ),
});

/**
 * Departments (fase 2, Settings). `department:list` is `yes` for every role — everybody sees the
 * groups, because a task carries one — so this route guards on nothing beyond being signed in.
 * The WRITES are what narrow: `department:update` and `:delete` are superadmin and admin.
 */
const departmentsSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/departments',
  component: lazyRouteComponent(
    () => import('@/features/settings/DepartmentsSettings'),
    'DepartmentsSettings',
  ),
});

/**
 * Tags (fase 2, Settings). Same shape as departments and for the same reason: `tag:list` is `yes`
 * for every role, so the route guards on nothing and the writes narrow inside the page. The panel
 * this renders is the one Contacts has mounted since Phase 1 — this route is where it now lives.
 */
/**
 * MEDLEMMER (A2, 22. oktober). Medlemsoppsettet flyttet hit fra Kontakter: legge til, rolle, tittel,
 * rapporterer til, medlem siden, invitasjoner, deaktivering og «Gi innlogging». `membership:list` er
 * cella som åpner det; endringene spør sine egne celler inne på siden. Filtrene er adressen, som før.
 */
const membersSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/members',
  validateSearch: (search: Record<string, unknown>): ContactsSearch => {
    const { tab: _tab, ...rest } = validateContactsSearch({ ...search, tab: 'organization' });
    return rest;
  },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'membership:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/settings/MembersSettings'),
    'MembersSettings',
  ),
});

const tagsSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/tags',
  component: lazyRouteComponent(() => import('@/features/settings/TagsSettings'), 'TagsSettings'),
});

/*
 * BYRÅKOBLINGER STO HER TIL 19. SEPTEMBER (QA12 #10).
 *
 * Siden sa: «Dere inviterer, de godtar — og først da går bud og forespørsler mellom husene.» Det
 * var sant, og det var kravet som gjorde at en forespørsel fra God Nok aldri nådde Standing
 * Ovation (#32). Atle: «Ingen egen innstilling for å koble et byrå. Bud og forespørsler skal gå
 * fram UANSETT.»
 *
 * Tabellen `organization_links` står — den bærer delte advancer og er historikk. Det som er borte
 * er KRAVET, og dermed siden som fantes for å oppfylle det. Ruting skjer på
 * `booking_agencies.linked_organization_id`, satt av plattformen (migrering 0251).
 */

/*
 * KONTAKTTYPER STO HER TIL 19. SEPTEMBER (QA12 #13/K-2). Atle: «Typen bør følge det byrået eller
 * personen selv har satt.» Migrering 0259 gjorde hver type som var i bruk til en tagg, 0292
 * slettet tabellen, og taggene ved siden av gjør nå jobben.
 */

/*
 * `/settings/delivery-categories` ER FJERNET (QA-runde 12, #14).
 *
 * Atle, 19. september: «Fjern siden fra org-innstillingene … Seksjonene kan gis nytt navn,
 * sorteres, legges til og fjernes i den enkelte advancen.» Gruppene bor på programposten etter
 * migrering 0262, og redigeres i advancens seksjonsvisning.
 *
 * Ruta er slettet og ikke satt til en videresending: en adresse som svarer er en adresse noen
 * bokmerker, og den ville pekt på et sted som ikke lenger er svaret.
 */

/**
 * `/settings/stage-times` OG `/settings/accreditation-types` STO HER TIL 21. SEPTEMBER
 * (QA13 runde 2, #5).
 *
 * Atle: «Begge sidene viser nå «Ingen årgang ennå – Oppsettet hører til en årgang» … Da er det
 * ingen grunn til å gå via org-innstillingene i det hele tatt. Fjern menypunktene og rutene …
 * Redigering skjer kun i årgangens Innstillinger.»
 *
 * Etter K-1 eier hver årgang sitt eget oppsett, og org-sidene var to skall som bare kunne si at
 * det ikke var noe å redigere. Ingen videresending, av samme grunn som står over ved
 * `/settings/delivery-categories`: en adresse som svarer er en adresse noen bokmerker, og den
 * ville pekt på et sted som ikke lenger er svaret.
 *
 * Komponentene `StageTimesSettings` og `AccreditationTypesSettings` lever videre — årgangens egne
 * innstillinger tegner dem. Det er bare inngangen fra organisasjonen som er borte.
 */

/**
 * Standardvilkår for budgivning (Atle, 4. september). Samme form som scenetidene:
 * `organization:update` strammer inne i siden, ruta krever bare at man er logget inn.
 */
const bookingTermsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/booking-terms',
  beforeLoad: ({ context }) => {
    /* Modulen Booking, spurt av can() (G1) — ikke typen. Samme spørsmål som menyen. */
    if (context.me) requirePermission(context.me, 'booking:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/settings/BookingTermsSettings'),
    'BookingTermsSettings',
  ),
});

/**
 * KONTRAKTER (sak 274, C4.2 og C8): fullmaktene og kontraktens tekster. `contract:read` åpner siden;
 * skrivingen strammes inne i siden (`organization:update`, `contract:manage_roles`).
 */
const contractSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/contracts',
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'contract:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/contracts/ContractSettingsPage'),
    'ContractSettingsPage',
  ),
});

/**
 * KONTRAKTEN FRA VARSELET (C9): slår opp siden kontrakten bor på for huset som leser
 * (`GET /contracts/:id/place`) og åpner skuffen der. Uten en side åpnes kontrakten her.
 */
const contractLandingRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/contracts/$contractId',
  staticData: { titleKey: 'contracts.title' },
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'contract:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/contracts/ContractLandingPage'),
    'ContractLandingPage',
  ),
});

/** Satser og scorevekter (Atle, 7. september). Samme form som standardvilkårene. */
const bookingPricingRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/booking-pricing',
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'booking:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/settings/BookingPricingSettingsPage'),
    'BookingPricingSettingsPage',
  ),
});

/**
 * ARRANGØRENS KONSERTER (agent D, SPILLESTED-KONSERT skjerm 5, 6, 8 og 11). `/shows` og ikke
 * `/concerts`: den adressen er artistens egne konserter (sak 216). Beholderen konsertene ligger i
 * har ingen adresse — en konsert er `/shows/$concertId`, aldri `/festivals/…`.
 */
const showsRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/shows',
  staticData: { navId: 'shows' },
  validateSearch: (search: Record<string, unknown>): ShowsSearch => validateShowsSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) {
      /*
       * Modulen Konserter, spurt av can() (G1). F24 — byrået ser sine solgte jobber her — er en følge
       * av modulen: Byrå tar alltid med Konserter (253.58, 0922).
       */
      requirePermission(context.me, 'concert:list');
    }
  },
  component: lazyRouteComponent(
    () => import('@/features/org-concerts/ConcertsListPage'),
    'ConcertsListPage',
  ),
});

const showNewRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/shows/new',
  staticData: { navId: 'shows', titleKey: 'shows.new.title' },
  beforeLoad: ({ context }) => {
    if (context.me) {
      requirePermission(context.me, 'concert:create');
    }
  },
  component: lazyRouteComponent(
    () => import('@/features/org-concerts/NewConcertPage'),
    'NewConcertPage',
  ),
});

const showDetailRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/shows/$concertId',
  staticData: { navId: 'shows', titleKey: 'shows.detail.title' },
  validateSearch: (search: Record<string, unknown>): ShowSearch => validateShowSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'concert:read');
  },
  component: lazyRouteComponent(
    () => import('@/features/org-concerts/ConcertDetailPage'),
    'ConcertDetailPage',
  ),
});

/**
 * SPILLESTEDER (agent B, SPILLESTED-KONSERT skjerm 2 og 3). Salene andre hus har lagt ut, salens
 * side utenfra og eget stedsregister. `venue:list` åpner siden, `venue:read` salen og
 * `venue_registry:list` registeret — alle «alle unntatt ekstern» eller strengere.
 */
const venuesRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venues',
  staticData: { navId: 'venues', titleKey: 'venueMarket.title' },
  validateSearch: (search: Record<string, unknown>): VenuesSearch => validateVenuesSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) {
      refuseGuest(context.me);
      requirePermission(context.me, 'venue:list', 'venues');
    }
  },
  component: lazyRouteComponent(() => import('@/features/venue-market/VenuesPage'), 'VenuesPage'),
});

const venueMarketRoomRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venues/rooms/$roomId',
  staticData: { navId: 'venues', titleKey: 'venueMarket.room.crumb' },
  validateSearch: (search: Record<string, unknown>): VenueRoomSearch =>
    validateVenueRoomSearch(search),
  beforeLoad: ({ context }) => {
    if (context.me) {
      refuseGuest(context.me);
      requirePermission(context.me, 'venue:read', 'venues');
    }
  },
  component: lazyRouteComponent(
    () => import('@/features/venue-market/VenueRoomPage'),
    'VenueRoomPage',
  ),
});

/*
 * QA15-S E: organisasjonens eget stedsregister er erstattet av plattformregisteret, som står i
 * Spillesteder sammen med salene. Den gamle adressen sender dit.
 */
const venueRegistryRoute = createRoute({
  getParentRoute: () => shellLayoutRoute,
  path: '/venues/register',
  staticData: { navId: 'venues', titleKey: 'venueMarket.title' },
  beforeLoad: ({ context }) => {
    if (context.me) {
      refuseGuest(context.me);
      requirePermission(context.me, 'venue:list', 'venues');
    }
    throw redirect({ to: '/venues', replace: true });
  },
  component: () => null,
});

/** Innstillinger › Konsertmaler (skjerm 12). */
const concertTemplatesSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/concert-templates',
  beforeLoad: ({ context }) => {
    if (context.me) requirePermission(context.me, 'concert_template:list');
  },
  component: lazyRouteComponent(
    () => import('@/features/org-concerts/ConcertTemplatesSettings'),
    'ConcertTemplatesSettings',
  ),
});

/**
 * ÅPNE DET SOM ER DELT (sak 232, T5): kortet i Innboks, raden i Kalender og en delt oppgave peker
 * hit. Ingen side av sin egen — den går i gjestemodus i organisasjonen som delte og sender videre
 * til den vanlige siden for objektet (`features/guest/SharedObjectPage.tsx`).
 *
 * UTENFOR SKALLET, under innloggingen: skallet ville tegnet personens egen organisasjon i det
 * øyeblikket fanen bytter, og `requireMembership` ville ha lagt fanen tilbake i den. `?from=` er
 * stien «Tilbake» går til, og den slippes bare gjennom som en intern sti.
 */
const sharedObjectRoute = createRoute({
  getParentRoute: () => authenticatedLayoutRoute,
  path: '/delt/$grantId',
  validateSearch: (search: Record<string, unknown>): { from?: string } => {
    const raw = search['from'];
    return typeof raw === 'string' && safeRedirect(raw) === raw ? { from: raw } : {};
  },
  beforeLoad: ({ context, location }) => {
    if (context.me) requireConsent(context.me, location.pathname);
  },
  component: lazyRouteComponent(
    () => import('@/features/guest/SharedObjectPage'),
    'SharedObjectRoute',
  ),
});

export const routeTree = rootRoute.addChildren([
  publicLayoutRoute.addChildren([signInRoute, forgotPasswordRoute]),
  resetPasswordRoute,
  authCallbackRoute,
  legalRoute,
  legalVersionRoute,
  publicSignupRoute,
  invitationRoute,
  shareLinkRoute,
  personalPageRoute,
  contractSigningRoute,
  joinRoute,
  artistScheduleRoute,
  connectConsentRoute,
  ...designRoutes,
  authenticatedLayoutRoute.addChildren([
    consentRoute,
    accountClosingRoute,
    noOrganizationRoute,
    sharedObjectRoute,
    shellLayoutRoute.addChildren([
      overviewRoute,
      inboxRoute,
      tasksRoute,
      contactsRoute,
      threadDetailRoute,
      taskDetailRoute,
      calendarRoute,
      concertsRoute,
      concertDetailRoute,
      sharedArtistsRoute,
      sharedArtistRoute,
      sharedProductionsRoute,
      showsRoute,
      showNewRoute,
      showDetailRoute,
      venuesRoute,
      venueMarketRoomRoute,
      venueRegistryRoute,
      contactDetailRoute,
      companyDetailRoute,
      bookingRoute,
      agencyArtistsRoute,
      artistRosterRoute,
      agencyAdvanceRoute,
      contractLandingRoute,
      documentsRoute,
      festivalRoute,
      editionRoute,
      editionPerformancesRoute,
      performanceDetailRoute,
      editionCalendarRoute,
      editionTasksRoute,
      editionPeopleRoute,
      editionResourcesRoute,
      editionMapRoute,
      editionSummaryRoute,
      editionDocumentsRoute,
      editionSettingsRoute,
      profileRoute,
      appearanceRoute,
      notificationPreferencesRoute,
      myShiftsRoute,
      exportsRoute,
      connectAiRoute,
      controlRoomLayoutRoute.addChildren([
        controlRoomOverviewRoute,
        controlRoomOrganizationsRoute,
        controlRoomOrganizationRoute,
        controlRoomAdminsRoute,
        controlRoomArtistScanRoute,
        controlRoomVenuesRoute,
        legacyOfficialRoute,
        legacyScanRoute,
        controlRoomOperationsRoute,
        controlRoomPermissionsRoute,
        controlRoomDefaultsRoute,
        controlRoomLegalRoute,
        controlRoomSoundchartsRoute,
        controlRoomVendorsRoute,
        controlRoomLogRoute,
        legacyFounderRoute,
        legacyFounderPermissionsRoute,
      ]),
      venueRoomsRoute,
      venueRoomRoute,
      venueNightsRoute,
      venueNightRoute,
      venueHouseRoute,
      venueEventsRoute,
      venueEventRoute,
      venueSettingsRoute,
      venuePeopleRoute,
      venueResourcesRoute,
      venueRequestsRoute,
      settingsLayoutRoute.addChildren([
        settingsIndexRoute,
        permissionsSettingsRoute,
        sharesSettingsRoute,
        aiSettingsRoute,
        organizationDetailsSettingsRoute,
        departmentsSettingsRoute,
        membersSettingsRoute,
        tagsSettingsRoute,
        settingsOrganizationRoute,
        settingsAccessRoute,
        settingsDataRoute,
        bookingTermsRoute,
        bookingPricingRoute,
        concertTemplatesSettingsRoute,
        contractSettingsRoute,
      ]),
    ]),
  ]),
]);

/** Every path the router serves. The navigation test asserts the menu points only at these. */
export const ROUTE_PATHS = [
  '/sign-in',
  '/forgot-password',
  '/reset-password',
  '/auth/callback',
  '/legal/$slug',
  '/legal/$slug/$version',
  '/skjema/$token',
  '/invitasjon/$token',
  '/lenke/$token',
  '/side/$token',
  '/signer/$token',
  '/bli-med/$token',
  /* B3/B5: godkjenningssiden for AI-tilkoblinger. */
  '/connect/consent',
  // Served only in a development build; listed so the menu test and the test router agree
  // with the router about every path that can exist.
  '/design',
  '/onboarding/consent',
  '/onboarding/organization',
  /* 284.5 (1151): siden med «Angre» for en konto som venter på slettingen. */
  '/konto/angre',
  /* Åpne det som er delt, i gjestemodus (sak 232, P5). */
  '/delt/$grantId',
  '/',
  '/inbox',
  '/tasks',
  '/contacts',
  '/calendar',
  '/concerts',
  /* «Mine vakter» står i menyen (QA13 103), så menytesten må kjenne adressen. */
  '/my-shifts',
  '/concerts/$kind/$id',
  /* Delte artister (A9) står i menyen. */
  '/shared-artists',
  '/shared-artists/$grantId',
  '/shared-productions',
  /* Arrangørens konserter (agent D). */
  '/shows',
  '/shows/new',
  '/shows/$concertId',
  /* Spillesteder (agent B). */
  '/venues',
  '/venues/rooms/$roomId',
  '/venues/register',
  '/inbox/$threadId',
  '/tasks/$taskId',
  '/contacts/$contactId',
  '/contacts/companies/$companyId',
  '/booking',
  /* Byråets artistside. Ruta finnes i begge hus; menyen skjuler den i en festival. */
  '/artists',
  '/artists/$artistId',
  /* Den delte advancen: festivalens programpost, sett fra byråets side av bordet (0133). */
  '/artists/$artistId/advance/$performanceId',
  /* Kontrakten fra varselet på bjella (C9). */
  '/contracts/$contractId',
  '/documents',
  '/festivals/$festivalId',
  '/festivals/$festivalId/editions/$editionId',
  '/festivals/$festivalId/editions/$editionId/performances',
  '/festivals/$festivalId/editions/$editionId/performances/$performanceId',
  // The other subpages every arrangement gets (NAVIGATION.md sone 2, Economy from sak 48, Kart
  // from KOI Del 2). `edition-routes.test.tsx` holds this block and `EVENT_NAV_ITEMS` to each
  // other, in both directions.
  '/festivals/$festivalId/editions/$editionId/calendar',
  '/festivals/$festivalId/editions/$editionId/tasks',
  '/festivals/$festivalId/editions/$editionId/people',
  '/festivals/$festivalId/editions/$editionId/resources',
  '/festivals/$festivalId/editions/$editionId/map',
  '/festivals/$festivalId/editions/$editionId/summary',
  '/festivals/$festivalId/editions/$editionId/documents',
  '/festivals/$festivalId/editions/$editionId/settings',
  '/profile',
  '/profile/appearance',
  '/profile/notifications',
  '/exports',
  /* B5: «Koble til AI», fra ikonet i toppen. */
  '/ai',
  /* Spillestedet sett fra huset selv (agent A). */
  '/venue/$festivalId/rooms',
  '/venue/$festivalId/rooms/$roomId',
  '/venue/$festivalId/nights',
  '/venue/$festivalId/nights/$nightId',
  /* Sak 229: spillestedet rundt arrangementer, uten årgang (V1). De to over videresender hit. */
  '/venue/$festivalId',
  '/venue/$festivalId/events',
  '/venue/$festivalId/events/$nightId',
  '/venue/$festivalId/settings',
  '/venue/$festivalId/people',
  '/venue/$festivalId/resources',
  '/venue/$festivalId/requests',
  '/settings',
  '/settings/permissions',
  '/settings/shares',
  /* B4: AI-bryterne og organisasjonens AI-tilkoblinger. */
  '/settings/ai',
  '/settings/organization-details',
  '/settings/departments',
  '/settings/members',
  '/settings/tags',
  /* `/settings/delivery-categories` sto her til 19. september. Se ruta over for hvorfor (#14). */
  '/settings/booking-terms',
  '/settings/booking-pricing',
  '/settings/concert-templates',
  '/settings/contracts',
  // The three folders. They serve a redirect rather than a page, and they are still paths the
  // router answers — the settings menu links at them.
  '/settings/organization',
  '/settings/access',
  '/settings/data',
  /* Control Room (sak 238), bare for en plattformadmin. De to siste er gründerportalens gamle stier. */
  '/control-room',
  '/control-room/organisasjoner',
  '/control-room/organisasjoner/$organizationId',
  '/control-room/plattformadminer',
  '/control-room/artistskanning',
  /* QA15-S E: registeret over spillesteder. */
  '/control-room/spillesteder',
  /* QA13 #3: de gamle adressene sender til Artistskanning. */
  '/control-room/offisiell-liste',
  '/control-room/byraskanning',
  '/control-room/drift',
  '/control-room/rettighetsvalg',
  '/control-room/standarder',
  '/control-room/vilkar',
  '/control-room/soundcharts',
  /* Atle 03.11: Leverandører. */
  '/control-room/leverandorer',
  '/control-room/logg',
  '/grunder',
  '/grunder/rettigheter',
] as const;
